import { Pagination, Input } from '@dsi/react-eds';
import ReactEDSWrapper from '../../../../utils/react-eds-wrapper';
import { useTheme } from '../../../../hooks/useTheme';
import { useEffect, useRef, useState } from 'react';
import { useLoader } from '../../../../hooks/useLoader';
import { useSwobNotifications } from '../../../../hooks/NotificationsProvider';
import { cnisConfigurationService } from '../../../../services/cnisConfigurationService';
import { AxiosError } from 'axios';
import { CnisReleasesNested } from './CnisConfigurationEntities';
import { useWebSocketService } from '../../../../hooks/useWebSocketService';
import styles from './CnisConfiguration.module.css';
import { useReactTable, getCoreRowModel, createColumnHelper, flexRender } from '@tanstack/react-table';
import type { ColumnHelper } from '@tanstack/react-table';
import { useDebounce } from '../../../../hooks/useDebounce';

const columnHelper: ColumnHelper<CnisReleasesNested> = createColumnHelper<CnisReleasesNested>();
function CnisConfigurationList() {
  const { theme } = useTheme();
  const { showLoader, hideLoader } = useLoader();
  const { addNotifications } = useSwobNotifications();
  const ws = useWebSocketService();

  const [releaseData, setReleaseData] = useState<CnisReleasesNested[]>([]);
  const [completeData, setCompleteData] = useState<CnisReleasesNested>();

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });
  const [searchInput, setSearchInput] = useState<string>('');
  const globalFilter = useDebounce(searchInput, 300);

  const fetchReleases = async (pageIndex: number, pageSize?: number, globalFilter?: string) => {
    showLoader();
    try {
      const size = pageSize || table.getState().pagination.pageSize;
      const res = await cnisConfigurationService.getPaginated({ limit: size, offset: pageIndex * size, global: globalFilter });
      table.setPageSize(res.data.limit);
      table.options.rowCount = res.data.total;
      table.options.pageCount = getPageCount(res.data.total, res.data.limit);
      setCompleteData(res.data);
      setReleaseData(res.data.data);
    } catch (error) {
      addNotifications([
        {
          title: 'Error - CNIS Configuration',
          icon: 'icon-triangle-warning',
          description:
            error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while fetching CNIS releases.',
          color: 'red',
          timestamp: new Date()
        }
      ]);
    } finally {
      setTimeout(() => {
        hideLoader();
      }, 500);
    }
  };

  const getPageCount = (total: number, limit: number) => {
    if (total % limit === 0) {
      return total / limit;
    } else {
      return Math.ceil(total / limit);
    }
  };

  useEffect(() => {
    const unsubscribe = ws?.onEntityChange('CNIS_RELEASE', (data) => {
      if (data) {
        const { pageIndex, pageSize } = pagination;
        fetchReleases(pageIndex, pageSize);
      } else {
        addNotifications([
          {
            title: 'Websocket Error',
            icon: 'icon-triangle-warning',
            description: 'An error occurred with the websocket. ',
            timestamp: new Date()
          }
        ]);
      }
    });

    fetchReleases(0, 10);

    return () => {
      unsubscribe?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    table.setPageIndex(0);
    fetchReleases(0, undefined, globalFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalFilter]);

  const getColumns = () => [
    columnHelper.accessor('release', {
      header: 'Release',
      cell: (info) => info.getValue()
    }),
    columnHelper.accessor('tickets', {
      header: 'Tickets',
      cell: (info) => info.getValue()
    }),
    columnHelper.accessor('createdAt', {
      header: 'Created',
      cell: (info) => new Date(info.getValue() as string).toISOString().split('T')[0]
    })
  ];

  const table = useReactTable({
    data: releaseData,
    columns: getColumns(),
    onPaginationChange: setPagination,
    manualPagination: true,
    rowCount: releaseData.length,
    getCoreRowModel: getCoreRowModel(),
    /*debugTable: true,
                debugHeaders: true,
                debugColumns: false,*/
    state: {
      //...
      pagination
    }
  });

  const getItemNumbers = () => {
    if (completeData) {
      return `${completeData?.total > completeData.limit ? completeData.data.length + completeData.offset : completeData.total} of ${completeData.total} items`;
    }
  };

  return (
    <>
      <div className={styles.settingsTitle}>Release Notes List</div>
      <p className={styles.subtitle}>View all the Release Notes created. </p>
      <div>
        <ReactEDSWrapper
          component={Input}
          style={{
            width: '30%'
          }}
          placeholder="Search..."
          icon="search"
          onChange={(e) => {
            setSearchInput(String((e.target as HTMLInputElement).value));
          }}
        />
        <span className={styles.itemNumbers}>{getItemNumbers()}</span>
      </div>
      <div className={`table ${styles.tableContainer}`}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse'
          }}
        >
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    style={{
                      textAlign: 'left',
                      padding: '12px 16px'
                    }}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          {completeData?.data.length === 0 && (
            <tbody>
              <tr>
                <td colSpan={5}>
                  <div className={styles.noDataContainer}>
                    <i className={`icon icon-info ${styles.dangerIcon}`} />
                    <div className={styles.noDataTitle}>No CNIS Release Notes Found</div>
                  </div>
                </td>
              </tr>
            </tbody>
          )}
          {completeData && completeData?.data?.length > 0 && (
            <tbody>
              {table.getPaginationRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  style={{
                    transition: 'background-color 0.2s ease'
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={{
                        padding: '12px 16px',
                        borderTop: `1px solid ${!theme ? '#404040' : '#242424'}`
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>
      <ReactEDSWrapper
        component={Pagination}
        numPages={table.getPageCount()}
        selectedPage={table.getState().pagination.pageIndex + 1}
        onPaginationChangePage={(page: number) => {
          table.setPageIndex(page - 1);
          fetchReleases(page - 1);
        }}
        optionsList={[
          { value: '10', label: '10', active: table.getState().pagination.pageSize === 10 },
          { value: '20', label: '20', active: table.getState().pagination.pageSize === 20 },
          { value: '30', label: '30', active: table.getState().pagination.pageSize === 30 },
          { value: '40', label: '40', active: table.getState().pagination.pageSize === 40 },
          { value: '50', label: '50', active: table.getState().pagination.pageSize === 50 }
        ]}
        onPaginationChangeSelect={(size: string) => {
          table.setPageSize(Number(size));
          table.setPageIndex(0);
          fetchReleases(0, Number(size));
        }}
        withActions
      />
    </>
  );
}
export default CnisConfigurationList;
